import clsx from "clsx";
import { returnReferenceRateOptions } from "@/lib/businessRules/referenceRateRule";
import { returnFormattedDate } from "@/lib/globalFormatterHelper";
import {
  OPTION_DELIVERIES,
  OPTION_EXERCISE_TYPES,
  OPTION_PREMIUM_TYPES,
  TradeDelivery,
  TradeOptionStyle,
  TradePremiumType,
} from "@/lib/interfaces and Enums/ENUMOptionTradeMessage";
import { TradeInterestType } from "@/lib/interfaces and Enums/ENUMSwapTradeMessage";
import { ISwapLegTicket, TicketInstrument } from "@/store/tradeFromScratchStore";
import {
  SwapLegKey,
  isFixedLeg,
  isZcsTicket,
  returnCpiCommitChanges,
  returnLegFrequencyChanges,
  returnEffectiveFirstCouponFrom,
  returnZcsCpiIndex,
} from "../helper/swapTicketHelper";
import { returnSwaptionLegOrientation } from "../helper/swaptionTicketHelper";
import { ITicketFieldsProps } from "../helper/ticketSharedHelper";
import { useSwapReferenceLists } from "../helper/useSwapReferenceLists";
import CalendarSelect from "../ticketFields/CalendarSelect";
import ReferenceRateSelect from "../ticketFields/ReferenceRateSelect";
import TicketDateInput from "../ticketFields/TicketDateInput";
import TicketLegRow, { LEG_ROW_GRID } from "../ticketFields/TicketLegRow";
import TicketNumberInput from "../ticketFields/TicketNumberInput";
import TicketTenorDateInput from "../ticketFields/TicketTenorDateInput";
import TicketRow from "../ticketFields/TicketRow";
import TicketSelect from "../ticketFields/TicketSelect";
import { useSwaptionTicket } from "./useSwaptionTicket";
import { returnLegHolidayCalendars, returnSettlementHolidayCalendars } from "../helper/holidayCalendarsHelper";
import { useZcsCpiAutoFill } from "../helper/useZcsCpiAutoFill";

// Option terms over a swap underlying. The defaults are real swaptions' (Cash, Yield,
// uncleared), not the swap's or bond option's - see SWAPTION_DEFAULT_*. Becomes the
// Trade Creation Security Details panel content.

const INTEREST_TYPES: TradeInterestType[] = [
  TradeInterestType.Fixed,
  TradeInterestType.Floating,
];

const legTestId = (key: SwapLegKey, name: string) =>
  `SwaptionTicket${key === "leg1" ? "Leg1" : "Leg2"}${name}Input`;

const SwaptionDetailFields = ({
  ticketId,
  highlightedKeys,
}: ITicketFieldsProps) => {
  const {
    ticket,
    updateSwaptionTicket,
    updateSwaptionLeg,
    handleLegConventionChange,
    dateIssue,
    requiredKeys,
  } = useSwaptionTicket(ticketId);
  // LQC-2957 - CPI filled from the backend for a ZCS underlying, unless typed.
  const cpiSourceNote = useZcsCpiAutoFill(ticket, updateSwaptionTicket);
  const referenceLists = useSwapReferenceLists();

  const isMissing = (key: string) => !!highlightedKeys?.has(key);
  const isRequired = (key: string) => requiredKeys.has(key);
  // Shown straight away - a date in the wrong order is wrong now.
  const dateErrorFor = (key: string) =>
    dateIssue?.key === key ? dateIssue.message : "";

  const legRow = (
    label: string,
    testId: string,
    field: keyof ISwapLegTicket,
    render: (leg: ISwapLegTicket, legKey: SwapLegKey) => React.ReactNode,
  ) => (
    <TicketLegRow
      label={label}
      testId={testId}
      isLeg1Highlighted={isMissing(`leg1.${field}`)}
      isLeg2Highlighted={isMissing(`leg2.${field}`)}
      isRequired={isRequired(`leg1.${field}`) || isRequired(`leg2.${field}`)}
      leg1={render(ticket.leg1, "leg1")}
      leg2={render(ticket.leg2, "leg2")}
    />
  );

  return (
    <div
      data-testid="SwaptionDetailFieldsContainer"
      id="SwaptionDetailFieldsContainer"
      className="flex flex-col gap-2"
    >
      <TicketRow
        label="Expiry Date"
        testId="SwaptionTicketExpiryDateRow"
        isHighlighted={isMissing("expiryDate")}
        isRequired={isRequired("expiryDate")}
        error={dateErrorFor("expiryDate")}
      >
        <TicketTenorDateInput
          testId="SwaptionTicketExpiryDateInput"
          holidayCalendars={returnSettlementHolidayCalendars(TicketInstrument.Swaption, ticket)}
          value={ticket.expiryDate}
          tenorStart={ticket.tradeDate}
          onChange={(expiryDate) => updateSwaptionTicket({ expiryDate })}
        />
      </TicketRow>

      <TicketRow label="Strike" testId="SwaptionTicketStrikeRow">
        <TicketNumberInput
          testId="SwaptionTicketStrikeInput"
          value={ticket.strike}
          placeholder="e.g. 3.25"
          onCommit={(strike) => updateSwaptionTicket({ strike })}
        />
      </TicketRow>

      {/* Spec F10: strikeQuotation is not shown, per Simon. It is still sent. */}
      <TicketRow label="Exercise Type" testId="SwaptionTicketExerciseTypeRow">
        <TicketSelect<TradeOptionStyle>
          testId="SwaptionTicketExerciseTypeInput"
          value={ticket.exerciseType}
          options={OPTION_EXERCISE_TYPES}
          includeEmptyOption={false}
          onChange={(exerciseType) => updateSwaptionTicket({ exerciseType })}
        />
      </TicketRow>

      <TicketRow label="Premium Type" testId="SwaptionTicketPremiumTypeRow">
        <TicketSelect<TradePremiumType>
          testId="SwaptionTicketPremiumTypeInput"
          value={ticket.premiumType}
          options={OPTION_PREMIUM_TYPES}
          includeEmptyOption={false}
          onChange={(premiumType) => updateSwaptionTicket({ premiumType })}
        />
      </TicketRow>

      <TicketRow label="Delivery" testId="SwaptionTicketDeliveryRow">
        <TicketSelect<TradeDelivery>
          testId="SwaptionTicketDeliveryInput"
          value={ticket.delivery}
          options={OPTION_DELIVERIES}
          includeEmptyOption={false}
          onChange={(delivery) => updateSwaptionTicket({ delivery })}
        />
      </TicketRow>

      <TicketRow
        label="Payment Ccy"
        testId="SwaptionTicketPaymentCurrencyRow"
        isHighlighted={isMissing("currencyPayment")}
        isRequired={isRequired("currencyPayment")}
      >
        <TicketSelect<string>
          testId="SwaptionTicketPaymentCurrencyInput"
          value={ticket.currencyPayment}
          options={referenceLists.currencies}
          includeEmptyOption={false}
          onChange={(currencyPayment) =>
            updateSwaptionTicket({ currencyPayment })
          }
        />
      </TicketRow>

      <TicketRow label="Trade Date" testId="SwaptionTicketTradeDateRow">
        <div className="text-xs dark:text-gray-200">
          {returnFormattedDate(ticket.tradeDate)}
        </div>
      </TicketRow>

      <TicketRow label="Cleared" testId="SwaptionTicketClearedRow">
        <input
          data-testid="SwaptionTicketClearedInput"
          id="SwaptionTicketClearedInput"
          type="checkbox"
          checked={ticket.cleared}
          onChange={(e) => updateSwaptionTicket({ cleared: e.target.checked })}
          className="h-4 w-4"
        />
      </TicketRow>

      {/* The underlying swap. Its cleared flag is separate from the option's. */}
      <div
        data-testid="SwaptionTicketUnderlyingHeader"
        id="SwaptionTicketUnderlyingHeader"
        className="border-b border-gray-300 pt-1 text-xs font-semibold dark:border-gray-600 dark:text-gray-200"
      >
        Underlying swap
      </div>

      <TicketRow
        label="Start Date"
        testId="SwaptionTicketStartDateRow"
        isHighlighted={isMissing("startDate")}
        isRequired={isRequired("startDate")}
      >
        <TicketDateInput
          testId="SwaptionTicketStartDateInput"
          holidayCalendars={returnLegHolidayCalendars(ticket)}
          value={ticket.startDate}
          onChange={(startDate) => updateSwaptionTicket({ startDate })}
        />
      </TicketRow>

      {/* Follows the start date until the user sets it (spec F10); one value, both legs. */}
      <TicketRow label="First Coupon From" testId="SwaptionTicketFirstCouponFromRow">
        <TicketDateInput
          testId="SwaptionTicketFirstCouponFromInput"
          value={returnEffectiveFirstCouponFrom(ticket)}
          onChange={(firstCouponFrom) => updateSwaptionTicket({ firstCouponFrom })}
        />
      </TicketRow>

      <TicketRow
        label="Maturity"
        testId="SwaptionTicketMaturityRow"
        isHighlighted={isMissing("maturityDate")}
        isRequired={isRequired("maturityDate")}
        error={dateErrorFor("maturityDate")}
      >
        <TicketTenorDateInput
          testId="SwaptionTicketMaturityInput"
          holidayCalendars={returnLegHolidayCalendars(ticket)}
          value={ticket.maturityDate}
          tenorStart={ticket.startDate}
          onChange={(maturityDate) => updateSwaptionTicket({ maturityDate })}
        />
      </TicketRow>

      <TicketRow label="Cleared" testId="SwaptionTicketUnderlyingClearedRow">
        <input
          data-testid="SwaptionTicketUnderlyingClearedInput"
          id="SwaptionTicketUnderlyingClearedInput"
          type="checkbox"
          checked={ticket.underlyingCleared}
          onChange={(e) =>
            updateSwaptionTicket({ underlyingCleared: e.target.checked })
          }
          className="h-4 w-4"
        />
      </TicketRow>

      {/* LQC-2959 - ZCS underlying only, as on Swap. Typed until CPI is sourced (B9/B10). */}
      {isZcsTicket(ticket) && (
        <>
          <TicketRow
            label="CPI"
            testId="SwaptionTicketCpiRow"
            isHighlighted={isMissing("cpi")}
          >
            <TicketNumberInput
              testId="SwaptionTicketCpiInput"
              value={ticket.cpi}
              placeholder="e.g. 131.2"
              onCommit={(cpi) => updateSwaptionTicket(returnCpiCommitChanges(ticket, cpi))}
            />
          </TicketRow>
          <TicketRow label="CPI Index" testId="SwaptionTicketCpiIndexRow">
            <div className="text-xs dark:text-gray-200">
              {returnZcsCpiIndex(ticket) || "-"}
              {cpiSourceNote && (
                <span className="ml-2 text-gray-500 dark:text-gray-400">({cpiSourceNote})</span>
              )}
            </div>
          </TicketRow>
        </>
      )}

      <div
        data-testid="SwaptionTicketLegHeader"
        id="SwaptionTicketLegHeader"
        className={clsx(
          LEG_ROW_GRID,
          "border-b border-gray-300 pt-1 dark:border-gray-600",
        )}
      >
        <div />
        {(["leg1", "leg2"] as SwapLegKey[]).map((key) => (
          <div
            key={key}
            data-testid={`SwaptionTicket${key === "leg1" ? "Leg1" : "Leg2"}Heading`}
            id={`SwaptionTicket${key === "leg1" ? "Leg1" : "Leg2"}Heading`}
            className="truncate px-1 text-xs font-semibold dark:text-gray-200"
          >
            {key === "leg1" ? "Leg 1" : "Leg 2"}
            {returnSwaptionLegOrientation(ticket, key) && (
              <span className="font-normal text-gray-500 dark:text-gray-400">
                {` · ${returnSwaptionLegOrientation(ticket, key)}`}
              </span>
            )}
          </div>
        ))}
      </div>

      {legRow("Type", "SwaptionTicketInterestTypeRow", "interestType", (leg, key) => (
        <TicketSelect<TradeInterestType>
          testId={legTestId(key, "InterestType")}
          value={leg.interestType}
          options={INTEREST_TYPES}
          includeEmptyOption={false}
          onChange={(interestType) =>
            handleLegConventionChange(key, { interestType })
          }
        />
      ))}

      {legRow("Currency", "SwaptionTicketCurrencyRow", "currency", (leg, key) => (
        <TicketSelect<string>
          testId={legTestId(key, "Currency")}
          value={leg.currency}
          options={referenceLists.currencies}
          includeEmptyOption={false}
          onChange={(currency) => handleLegConventionChange(key, { currency })}
        />
      ))}

      {/* No spread row: the swaption leg model has no such field. */}
      {legRow("Coupon", "SwaptionTicketRateRow", "couponRate", (leg, key) =>
        isFixedLeg(leg) ? (
          <TicketNumberInput
            testId={legTestId(key, "Rate")}
            value={leg.couponRate}
            placeholder="e.g. 2.675"
            onCommit={(couponRate) => updateSwaptionLeg(key, { couponRate })}
          />
        ) : (
          <div className="px-1 text-xs text-gray-400">-</div>
        ),
      )}

      {legRow(
        "Reference Rate",
        "SwaptionTicketReferenceRateRow",
        "referenceRate",
        (leg, key) =>
          isFixedLeg(leg) ? (
            <div className="px-1 text-xs text-gray-400">-</div>
          ) : (
            <ReferenceRateSelect
              testId={legTestId(key, "ReferenceRate")}
              value={leg.referenceRate}
              currency={leg.currency}
              options={returnReferenceRateOptions(
                leg.currency,
                isZcsTicket(ticket),
                leg.couponFrequency,
              )}
              onChange={(referenceRate) =>
                updateSwaptionLeg(key, { referenceRate })
              }
            />
          ),
      )}

      {legRow("Frequency", "SwaptionTicketFrequencyRow", "couponFrequency", (leg, key) => (
        <TicketSelect<string>
          testId={legTestId(key, "Frequency")}
          value={leg.couponFrequency}
          options={referenceLists.frequencies}
          includeEmptyOption={false}
          onChange={(couponFrequency) =>
            updateSwaptionTicket(
              returnLegFrequencyChanges(ticket, key, couponFrequency),
            )
          }
        />
      ))}

      {legRow("Reset Freq", "SwaptionTicketResetFrequencyRow", "resetFrequency", (leg, key) =>
        isFixedLeg(leg) ? (
          <div className="px-1 text-xs text-gray-400">-</div>
        ) : (
          <TicketSelect<string>
            testId={legTestId(key, "ResetFrequency")}
            value={leg.resetFrequency}
            options={referenceLists.frequencies}
            includeEmptyOption={false}
            onChange={(resetFrequency) => updateSwaptionLeg(key, { resetFrequency })}
          />
        ),
      )}

      {legRow("Day Count", "SwaptionTicketDayCountRow", "dayCount", (leg, key) => (
        <TicketSelect<string>
          testId={legTestId(key, "DayCount")}
          value={leg.dayCount}
          options={referenceLists.dayCounts}
          includeEmptyOption={false}
          onChange={(dayCount) => updateSwaptionLeg(key, { dayCount })}
        />
      ))}

      {legRow("Business Day", "SwaptionTicketBusinessDayRow", "businessDay", (leg, key) => (
        <TicketSelect<string>
          testId={legTestId(key, "BusinessDay")}
          value={leg.businessDay}
          options={referenceLists.businessDays}
          includeEmptyOption={false}
          onChange={(businessDay) => updateSwaptionLeg(key, { businessDay })}
        />
      ))}

      {legRow("Calendar", "SwaptionTicketCalendarRow", "calendar", (leg, key) => (
        <CalendarSelect
          testId={legTestId(key, "Calendar")}
          value={leg.calendar}
          calendars={referenceLists.calendars}
          isReady={referenceLists.isCalendarListReady}
          onChange={(calendar) => updateSwaptionLeg(key, { calendar })}
        />
      ))}
    </div>
  );
};

export default SwaptionDetailFields;
